<template>
  <div class="canvas-templates-container h-full">
    <div ref="toolbarWrapper" class="sticky z-100">
      <Navbar />
      <CSearchFilters
        v-model:filters="filters"
        class="mt-3"
        :disabled="isFetchingTemplates"
        entity="canvas"
        hide-metadata-filters
        is-template
        :placeholder="t('canvasUI.canvasSelector.canvasTemplates.searchByNameOrTag')"
        @update:filters="fetchTemplatesAfterFiltering"
        @update:search="handleSearch"
      />

      <SectionSelectorToolbar
        :are-all-selected="areAllSelected"
        :are-some-selected="areSomeSelected"
        :current-page="currentPage"
        :first-page-total-displayed-items="firstPageTotalDisplayedTemplates ?? 0"
        :loading="isFetchingTemplates"
        :page-size="pageSize"
        :total-items="availableTemplatesTotalCount"
        :total-selected="selectedCanvasTemplates.length"
        @page-first="fetchFirstChunk"
        @page-last="fetchLastChunk"
        @page-next="fetchNextChunk"
        @page-previous="fetchPreviousChunk"
        @toggle-select-all="toggleSelectAll"
      />
    </div>
    <div v-if="isFetchingTemplates" class="pt-3 overflow-auto" :style="{ height: wrapperHeight }">
      <NSkeleton v-for="n in 8" :key="n" class="w-full rounded" :class="{ 'mt-1': n > 1 }" :height="135" />
    </div>
    <CList
      v-else-if="orderedAvailableTemplates?.length"
      class="rounded-2 flex bg-base overflow-y-auto"
      :data="orderedAvailableTemplates"
      :height="parseInt(wrapperHeight)"
      :maxRowHeight="150"
      :selectedIds="selectedCanvasTemplates.map((t) => t.id)"
      @item:click="onTemplateSelect"
    >
      <template #item="{ item, isSelected, index }">
        <CanvasListItem
          :canvas="item"
          :data="orderedAvailableTemplates"
          :filters="filters"
          hide-popularity-icons
          :index="index"
          :is-selected="isSelected"
          :item="item"
          :selected-items="selectedCanvasTemplates"
        />
      </template>
    </CList>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject, type ComputedRef, type ToRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { NSkeleton } from 'naive-ui'

import CSearchFilters from '@lib/components/CSearchFilters/CSearchFilters.vue'
import CList from '@lib/components/CList/CList.vue'
import CanvasListItem from './CanvasListItem.vue'
import SectionSelectorToolbar from '@canvas-builder/components/ui/SectionList/SectionSelectorToolbar.vue'
import useAppStore from '@canvas-selector/stores/app'
import type { Canvas } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { AppProps } from '@canvas-selector/appTypes'
import Navbar from './Navbar.vue'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import { useElementHeightResize } from '@lib/composables/useElementHeightResize'

const { t } = useI18n()
const { data, selectedItems, selectedCanvasTemplates } = useAppStore()

const appProps = inject<ToRefs<AppProps>>('appProps')
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pageSize = computed(() => launchDarkly.value.canvas_selector_page_size || 500)
const filters = ref<Record<string, any>>({})
const isFetchingTemplates = ref(false)
const search = ref('')
const currentPage = ref(1)
const availableTemplatesTotalCount = ref(0)
const firstPageTotalDisplayedTemplates = ref<number | undefined>()
const toolbarWrapper = ref<HTMLElement | null>(null)
const { wrapperHeight } = useElementHeightResize(toolbarWrapper, calculateWrapperHeight)
const orderedAvailableTemplates = computed(() => {
  return data.value?.canvasTemplates ?? []
})

const areAllSelected = computed(
  () =>
    !!data.value?.canvasTemplates?.every((template) => selectedCanvasTemplates.value.some((t) => t.id === template.id)),
)

const areSomeSelected = computed(
  () =>
    !!(
      data.value?.canvasTemplates?.some((template) =>
        selectedCanvasTemplates.value.some((t) => t.id === template.id),
      ) && !areAllSelected.value
    ),
)

const fetchTemplates = async () => {
  if (!appProps?.listCanvases) return

  isFetchingTemplates.value = true
  try {
    const response = await appProps.listCanvases.value({
      is_template: true,
      is_section: false,
      search: search.value,
      filters: normalizeFilterParams(filters.value),
      page: currentPage.value,
      page_size: pageSize.value,
    })
    data.value = { ...data.value, canvasTemplates: response.results }
    availableTemplatesTotalCount.value = response.count!
    firstPageTotalDisplayedTemplates.value = response.results?.length
  } catch (error) {
    console.error('Failed to fetch canvas templates', error)
  } finally {
    isFetchingTemplates.value = false
  }
}

const handleSearch = (newValue: string) => {
  search.value = newValue
  currentPage.value = 1
  fetchTemplates()
}

const fetchTemplatesAfterFiltering = () => {
  currentPage.value = 1
  fetchTemplates()
}

const fetchNextChunk = () => {
  currentPage.value++
  fetchTemplates()
}

const fetchPreviousChunk = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchTemplates()
  }
}

const fetchFirstChunk = () => {
  currentPage.value = 1
  fetchTemplates()
}

const fetchLastChunk = () => {
  if (availableTemplatesTotalCount.value > 0) {
    currentPage.value = Math.ceil(availableTemplatesTotalCount.value / pageSize.value)
    fetchTemplates()
  }
}

const onTemplateSelect = (template: Canvas) => {
  const index = selectedItems.value.findIndex((t) => t.id === template.id)
  if (index !== -1) {
    selectedItems.value.splice(index, 1)
  } else {
    selectedItems.value.push(template)
  }
}

const toggleSelectAll = () => {
  if (areAllSelected.value) {
    selectedItems.value = selectedItems.value.filter(
      (t) => !data.value?.canvasTemplates?.some((template) => t.id === template.id),
    )
  } else {
    selectedItems.value = [
      ...selectedItems.value,
      ...(data.value?.canvasTemplates?.filter(
        (t) => !selectedItems.value.some((selectedTemplate) => selectedTemplate.id === t.id),
      ) ?? []),
    ]
  }
}

function calculateWrapperHeight(height: number) {
  return `${(toolbarWrapper.value?.parentElement?.parentElement?.offsetHeight || 0) - (height || 0) - 20}px`
}

onMounted(fetchTemplates)
</script>
